import * as Sentry from '@sentry/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router } from 'react-router-dom'
import { MantineProvider } from '@mantine/core'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { parse, stringify } from 'query-string'

import { LemonadeProvider, ErrorBoundaryFallback } from '@percent/lemonade'
import { goodstackPalette } from '@percent/partner-dashboard/app/theme/Theme'
import { logger } from '@percent/partner-dashboard/capabilities/logger'
import { WithAxios } from '@percent/partner-dashboard/common/HOC/withAxios/WithAxios'
import { AuthContextController } from '@percent/partner-dashboard/context/auth'
import { env } from '@percent/partner-dashboard/env'
import { App } from './app/app-entry'
import { PostHogProvider } from './app/providers/PostHogProvider/PostHogProvider'
import { CountriesContextController } from './context/countries/CountriesContextController'
import { abilityDefinitions } from './context/iam/abilityDefinitions'
import { IamContextController } from './context/iam/iamContext/IamContextController'
import { ServiceContextProvider } from './context/serviceContext/ServiceContextProvider'

if (env.NX_PUBLIC_USE_SENTRY_LOGGER === true) {
  Sentry.init({
    dsn: 'https://6495fb4ae40e4e478aa27062e73077d3@o128854.ingest.sentry.io/5379754',
    environment: env.NX_PUBLIC_REACT_APP_ENV,
    autoSessionTracking: true,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.33
  })
}

const container = document.getElementById('root')
const root = createRoot(container!)

root.render(
  <React.StrictMode>
    <Router>
      <Sentry.ErrorBoundary
        fallback={<ErrorBoundaryFallback />}
        onError={(error: unknown) => {
          logger(error as Error)
        }}
      >
        <MantineProvider>
          <LemonadeProvider customPalette={goodstackPalette}>
            <QueryParamProvider
              adapter={ReactRouter6Adapter}
              options={{
                searchStringToObject: parse,
                objectToSearchString: stringify
              }}
            >
              <ServiceContextProvider>
                <AuthContextController>
                  <PostHogProvider>
                    <IamContextController rulesList={abilityDefinitions}>
                      <CountriesContextController>
                        <WithAxios>
                          <App />
                        </WithAxios>
                      </CountriesContextController>
                    </IamContextController>
                  </PostHogProvider>
                </AuthContextController>
              </ServiceContextProvider>
            </QueryParamProvider>
          </LemonadeProvider>
        </MantineProvider>
      </Sentry.ErrorBoundary>
    </Router>
  </React.StrictMode>
)
